import {
    Component,
    Inject,
    OnDestroy,
    OnInit,
    HostListener,
    ViewChild,
    ViewChildren,
    QueryList,
    ElementRef,
    TemplateRef,
    Injector} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { APP_BASE_HREF } from '@angular/common';

import { Subscription, filter, of, from, BehaviorSubject, tap, catchError, switchMap, map } from 'rxjs';
import moment from 'moment-timezone';

import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { validateEmail, validateEmails, emailSeparatorsRegex } from '../../../../Common/Misc/helpers/formatting/email.helper';
import { PublishMetadataModel } from '../../../../Common/Dialogs/models/publish-metadata.model';
import { PublishStoryComponentTextModel } from '../../../../Common/Dialogs/models/publish-story-component-text.model';
import { DropdownOptionModel } from '../../../../Common/Forms/models/dropdown-option.model';
import { AttributeModel } from '../../../../Common/Misc/models/attribute.model';
import { PreferencesModel } from '../../../../Core/Users/models/preferences.model';
import { TimeHelper } from '../../../../Common/Forms/helpers/time.helper';
import { attributeTypesEnum } from '../../../../Common/Misc/enums/attribute-types.enum';
import { TabComponent } from '../../../../Common/Layout/components/Tab/tab.component';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { PublishStoryComponent } from '../../../../Common/Dialogs/components/PublishStory/publish-story.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { PublishStoryDialogModel } from '../../../../Common/Dialogs/models/publish-story-dialog.model';
import { MentionSearchService } from '../../../../Monitor/Core/services/mention-search.service';
import { FocusHelper } from '../../../../Common/Forms/helpers/focus.helper';

import { TopicsService } from '../../../Core/services/topics.service';
import { DeliveryRangeModel } from '../../../../Common/Forms/models/delivery-range.model';

import { BlockModel } from '../../../../Common/PageBuilder/models/block.model';
import { TextDisplayElementModel } from '../../../../Common/PageBuilder/models/text-display-element.model';
import { PageBuilderComponent } from '../../../../Common/PageBuilder/components/PageBuilder/page-builder.component';

import { BriefingStatusEnum } from '../../../../Common/MonitorShared/enum/briefing-status.enum';
import { BriefingDetailsEnum } from '../../../Briefings/enums/briefing-details.enum';
import { BriefingDateRangeModel } from '../../../Briefings/models/briefing-date-range.model';

import { AlertsService } from '../../services/alerts.service';
import { AlertModel } from '../../models/alert.model';
import { AlertBlockTypeEnum } from '../../enums/alert-block-type.enum';
import { RightSidebarComponent } from '../../../../Common/Layout/components/RightSidebar/right-sidebar.component';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { MonitoringSourcePickerFieldModel } from '../../../Shared/models/monitoring-source-picker-field.model';
import { MonitoringSeriesTypeEnum } from '../../../Shared/enums/monitoring-series-type.enum';
import { MonitoringBackEndDataSourceModel } from '../../../Shared/models/monitoring-back-end-data-source.model';
import { AbstractPageBuilderService } from '../../../../Common/PageBuilder/services/abstract-page-builder.service';
import { RSSFeedService } from '../../../Shared/services/rss-feed.service';
import { deliveryOptionsEnum } from '../../../Shared/enums/delivery-options.enum';
import { monitoringFiltersEnum } from '../../../Shared/enums/monitoring-filters.enum';
import {
    MonitoringSourcePickerFieldWithoutFacetComponent
} from '../../../Shared/components/MonitoringSourcePickerField/monitoring-source-picker-field-without-facet.component';
import { AgprComponentStyleEnum } from '../../../../Common/Basic/enums/social-analysis-types.enum';
import { AlertBannerErrorTypeEnum, bannerErrorsMap } from '../../../../Monitor/Alerts/enums/alert-banner-error-type.enum';
import { NewResourceDialogModel } from '../../../../Common/Dialogs/models/new-resource-dialog.model';
import { NewResourceDialogComponent } from '../../../../Common/Dialogs/components/NewResourceDialog/new-resource-dialog.component';
import { TextInputDialogComponent } from '@Common/Dialogs/components/TextInputDialog/text-input-dialog.component';
import { TextInputDialogModel } from '@Common/Dialogs/models/text-input-dialog.model';
import { AlertSurgeCooldownEnum } from '@Monitor/Alerts/enums/alert-surge-cooldown.enum';
import { AlertSurgeMetricEnum } from '@Monitor/Alerts/enums/alert-surge-metric.enum';
import { alertSurgeCooldownOptions } from '@Monitor/Alerts/models/alert-surge-cooldown-options.model';
import { FullProfileAnimationModel } from '@Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '@Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '@Core/Dialogs/enums/overlay-animation.enum';
import { AlertSurgeSensitivityDialogComponent } from '../AlertSurgeSensitivityDialog/alert-surge-sensitivity-dialog.component';
import { AlertSurgeDisplayElementModel } from '@Monitor/Alerts/models/alert-surge-display-element.model';
import { BriefingBlockTypeEnum } from '../../../Briefings/enums/briefing-block-type.enum';
import { alertSurgeMetricOptions } from '@Monitor/Alerts/models/alert-surge-metric-options.model';
import { MentionSearchCriteriaModel } from '@Monitor/Shared/models/mention-search-criteria.model';
import { BriefingConfigService } from '@Common/MonitorShared/services/briefing-config.service';
import { AlertLimitReachedDialogComponent } from '../AlertLimitReachedDialog/alert-limit-reached.component';
import { AlertTypeEnum } from '../../../../Common/MonitorShared/enum/alert-type.enum';
import { BriefingConfigApiService } from '@api/monitoring/briefing-config.api.service';
import { gateCall } from '@Core/rxjs-utils';
import { DisplayGroupModel } from '../../../../Common/Layout/models/display-group.model';
import { ConfirmDialogComponent } from '../../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { BriefingImageDisplayElementModel } from '@Common/PageBuilder/models/briefing-image-display-element.model';
import { ImageSourceEnum } from '../../../../Common/PageBuilder/enums/image-source.enum';
import { CuratorBlock } from '@Common/PageBuilder/models/curator-block.model';
import { LayoutBlock } from '@Common/PageBuilder/models/layout-block.model';
import { AlertSeriesService } from '../../services/alert-series.service';
import { BriefingChartDisplayElementModel } from '@Common/PageBuilder/models/briefing-chart-display-element.model';
import { AnalysisDisplayElementModel } from '../../../Briefings/models/briefing-analysis-display-element.model';
import { MAX_BASE_64_IMAGE_SIZE } from '../../../../Core/constants';
import { BannerErrorConfigModel } from '../../../../Common/Misc/models/banner-error-config.model';


export enum RightSidebarTabButtons {
    alertDetail = 0,
    editBlock = 1
}

export enum AlertActionsEnum {
    activate = 'activate',
    pause = 'pause',
    renew = 'renew',
}

@Component({
    selector: 'agpr-alert',
    templateUrl: './alert.component.html',
    styleUrls: ['./alert.component.scss'],
    providers: [
        {
            provide: AbstractPageBuilderService,
            useExisting: AlertsService
        },
        MentionSearchService,
    ],
    standalone: false
})
export class AlertComponent implements OnInit, OnDestroy {

    @ViewChild('blockFormTab') blockFormTab: TabComponent;
    @ViewChild('rhp') rhp: RightSidebarComponent;
    @ViewChildren('sidebarError', {read: ElementRef}) sidebarErrors: QueryList<ElementRef>;
    @ViewChild('rssFeedDisclaimer') rssFeedDisclaimer: TemplateRef<any>;
    @ViewChild(PageBuilderComponent) pageBuilder: PageBuilderComponent;
    @ViewChild('sourcePicker') sourcePicker: MonitoringSourcePickerFieldWithoutFacetComponent;

    alert: AlertModel;
    isDirty: boolean = false;
    isActive: boolean = false;
    attributes: AttributeModel[];

    selectedSource: MonitoringBackEndDataSourceModel;
    sourceDeleted: boolean = false;

    disableSaveButton: boolean = false;

    showErrors: boolean = false;
    isShowBannerMessage: boolean = false;
    bannerErrorList: BannerErrorConfigModel[] = [];
    isBannerExpanded: boolean = false;

    preferences: PreferencesModel;
    showErrorsLevel: number = 0;
    selectedTabIndex: number = 0;
    selectedBlock: BlockModel;

    /** A list of possible errors that should show their messages */
    fieldErrors: {[key: string]: boolean} = {
        noSource: false,

        nameEmpty: false,
        nameLength: false,

        subjectEmpty: false,
        subjectLength: false,

        fromNameEmpty: false,
        fromNameLength: false,

        replyToEmpty: false,
        replyToLength: false,
        replyToInvalidFormat: false,

        recipientsEmpty: false,

        rssFeedEmpty: false,

        timeRangePast: false,
        timeRangeIncomplete: false,
        endDateLessThanStartDate: false,
        invalidFilter: false
    };

    activateDialogMetadata: PublishMetadataModel[] = [];
    activateDialogLocalization: PublishStoryComponentTextModel = {
        confirmationInformation: 'Alerts.activateDialog.heading',
        authorTitle: '',
        iAgree: 'Alerts.activateDialog.iAgree',
        agreePublish: 'Alerts.activateDialog.agreeAndSubmit',
        cancel: 'cancel',
        confirmationMessage: '',
        confirmation: '',
        publish: '',
        headerTitle: 'Alerts',
        headerSubTitle: 'Alerts.activateDialog.subtitleLine1',
        headerContent: 'Alerts.activateDialog.subtitleLine2',
        publishLoader: 'Alerts.activateDialog.publishingLoading'
    };

    timeRange: DeliveryRangeModel;
    minEndDate: Date;
    sourceLoaded: boolean = false;

    topicHasProximity: boolean = false;

    errorClass = '.agpr-form-message';
    showDeliveryOptionsErrorMsg: boolean = false;

    metric: AlertSurgeMetricEnum;
    sensitivity: number;
    coolDownPeriod: AlertSurgeCooldownEnum;
    overlayContainer: Element;

    briefingConfigRequest: Subscription;

    filterError: string = '';

    // @TODO unhide to add precision is added to alerts
    // precisionClipProcess: PrecisionClipProcess;

    private defaultSelectedTabIndex: number = 0;
    private blockFormTabIndex: number = 2;
    private subscriptions: Subscription[] = [];

    readonly id: string = 'alerts';
    readonly noErrors: number = 0;
    readonly draftErrors: number = 1;
    readonly sendErrors: number = 2;
    readonly severity = messageSeverityEnum;
    readonly buttonTypes = ButtonTypesEnum;
    readonly briefingStatus = BriefingStatusEnum;
    readonly deliveryOptionsEnum = deliveryOptionsEnum;
    readonly agprComponentStyleEnum = AgprComponentStyleEnum;
    readonly rightSidebarTabButtons = RightSidebarTabButtons;
    readonly alertTypes = AlertTypeEnum;
    readonly cooldownTypesOptions = alertSurgeCooldownOptions;
    readonly metricTypesOptions = alertSurgeMetricOptions;
    /** Max number of characters for each of the main alert form fields */
    readonly maxInputTextLength: number = 256;
    readonly validateEmails: Function = validateEmails;
    readonly emailsSeparatorRegex: RegExp = emailSeparatorsRegex;
    readonly alertFilters: monitoringFiltersEnum[] = [
        monitoringFiltersEnum.keywords,
        monitoringFiltersEnum.authors,
        monitoringFiltersEnum.outlets,
        monitoringFiltersEnum.audience,
        monitoringFiltersEnum.ave,
        monitoringFiltersEnum.sentiment,
        monitoringFiltersEnum.mediaTypes,
        monitoringFiltersEnum.regions,
        monitoringFiltersEnum.languages,
        monitoringFiltersEnum.hashtags,
        monitoringFiltersEnum.sourceGroups,
        monitoringFiltersEnum.discoverLists,
    ];

    readonly sourcePickerFieldModel = new MonitoringSourcePickerFieldModel(
        null,
        'id',
        '',
        '',
        {
            dataSourceLabel: 'Monitoring.sourcePickerField.coverageSource',
            dateRangeLabel: 'Monitoring.sourcePickerField.dateRange',
            mentionCountLocalizationLabel: 'Monitoring.sourcePickerField.briefingsResultFeedbackMessage',
            maxNumberOfMentions: 1000,
            dataSourceNullErrorMessage: 'Monitoring.sourcePickerField.dataSourceRequiredMessage',
            dataSourceOptions: [
                new DropdownOptionModel('Reports.series.type.topic', MonitoringSeriesTypeEnum.topic),
                new DropdownOptionModel('Reports.series.type.subscription', MonitoringSeriesTypeEnum.subscription),
                new DropdownOptionModel('Reports.series.type.socialTopic', MonitoringSeriesTypeEnum.socialTopic),
                new DropdownOptionModel('Reports.series.type.theme', MonitoringSeriesTypeEnum.theme)
            ],
            showResultsFeedback: false,
            showLoadingFeedback: false,
            showTooManyMentionsError: false,
            showFilters: true,
            showDateRange: false,
            showViewSource: true,
            orderedAccountFilters: this.alertFilters,
            orderedMercuryFilters: this.alertFilters
        }
    );

    constructor(
        public alertsService: AlertsService,
        public localizationService: LocalizationService,
        public topicsService: TopicsService,
        public currentUserStateService: CurrentUserStateService,
        public timeHelper: TimeHelper,
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        private coreDialogService: CoreDialogService,
        private rssFeedService: RSSFeedService,
        public titleService: Title,
        public injector: Injector,
        public briefingConfigService: BriefingConfigService,
        private briefingAccountApi: BriefingConfigApiService,
        private alertSeriesService: AlertSeriesService,
    ) {}

    ngOnInit(): void {
        this.subscriptions.push(
            this.alertsService.getCurrentAlert().pipe(
                filter(x => !!x),
                tap((alert => {
                    let differentAlert = false;
                    if (!this.alert || !alert || this.alert.id === null || this.alert.id !== alert.id) {
                        differentAlert = true;
                    }
                    this.alert = alert;

                    this.isActive = this.alert && this.alert.status === BriefingStatusEnum.ACTIVE;

                    this.updateTimeRangeFromAlert();

                    if (differentAlert) {
                        this.resetValidation();
                    } else {
                        this.validate(this.showErrorsLevel);
                    }
                    if (alert) {
                        this.titleService.setTitle(
                            this.localizationService.transform('Monitoring.alerts.browserTitle.name', {name: alert.name})
                        );

                        if (alert.alertIsSubscriptionSearch) {
                            this.selectedSource = new MonitoringBackEndDataSourceModel(MonitoringSeriesTypeEnum.subscription);
                        } else if (alert.alertIsThemeSearch) {
                            this.selectedSource = new MonitoringBackEndDataSourceModel(MonitoringSeriesTypeEnum.theme);
                        } else if (alert.alertIsAgentSearch) {
                            this.selectedSource = new MonitoringBackEndDataSourceModel(MonitoringSeriesTypeEnum.socialTopic);
                        } else if (alert.alertIsAiAgentSearch) {
                            this.selectedSource = new MonitoringBackEndDataSourceModel(MonitoringSeriesTypeEnum.aiAgent);
                        } else {
                            this.selectedSource = new MonitoringBackEndDataSourceModel(MonitoringSeriesTypeEnum.topic);
                        }
                        this.selectedSource.id = alert.searchCriteriaId;
                        this.selectedSource.filterSearchCriteria = this.alert.filterSearch.searchCriteria;

                        if (this.sourcePicker) {
                            this.sourcePicker.getFiltersFromSearch(this.selectedSource.filterSearchCriteria);
                        }
                    }
                })),
                switchMap(alert => this.rssFeedService.getByIds(alert?.getFeedIds()))
            ).subscribe(rssFeeds  => {
                this.alert.setFeedNameAndUrl(rssFeeds);
                this.getAttributes();
                this.getSurgeValues();
                this.alertChanged();
            }),
            this.alertsService.getSelectedBlock().subscribe((block) => {
                this.selectedBlock = block;

                if (this.selectedBlock) {
                    // HACK: changing the tab index here doesn't actually select it unless the block form tab is already enabled
                    // This is because the disabled property in the template is handled after the tab index
                    // So we change the disabled state manually to handle that

                    if (this.blockFormTab) {
                        this.blockFormTab.disabled = false;
                    }
                    this.selectedTabIndex = this.blockFormTabIndex;

                    const analysisDisplayElement = this.selectedBlock.displayElement as AnalysisDisplayElementModel;
                    const isAnalysisBlockWithContent = this.selectedBlock.type.key === AlertBlockTypeEnum.ANALYSIS
                        && !analysisDisplayElement.generateOnSend
                        && !!analysisDisplayElement.text?.trim();

                    if (this.selectedBlock.type.key !== AlertBlockTypeEnum.TEXT && !isAnalysisBlockWithContent) {
                        const formSelector = 'agpr-block-form';

                        requestAnimationFrame(() => {
                            const blockForm = document.querySelector(formSelector);
                            if (blockForm) {
                                const firstInput: HTMLElement = blockForm.querySelector('input, button, [role=button], textarea');
                                if (firstInput) {
                                    firstInput.focus();
                                }
                            }
                        });
                    }
                } else if (this.selectedTabIndex === this.blockFormTabIndex) {
                    this.selectedTabIndex = this.defaultSelectedTabIndex;
                }
            }),
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(
                preferences => {
                    if (preferences) {
                        this.preferences = preferences;
                        this.updateTimeRangeTimezoneFromPreferences();
                    }
                }
            ),
            this.alertsService.filterError$.subscribe(x => {
                if (x) {
                    this.filterError = x;
                    this.validate(this.draftErrors);
                } else {
                    this.filterError = '';
                }
            }),
            this.alertsService.getBlockDisplayElementChanged().subscribe(() => {
                if (this.alert) {
                    this.setBannerErrorList(this.showErrorsLevel);
                    if (!this.bannerErrorList?.length) {
                        this.isShowBannerMessage = false;
                    }
                }
            }),
            this.alertsService.refreshChartBlockData().pipe(
                filter(x => !!x && x.length > 0),
                switchMap(x => from(this.alertSeriesService.resetSeries(x)).pipe(map(() => x))),
                switchMap(x => this.alertSeriesService.prepareSeries(x).pipe(map(() => x))),
                tap((blocks) => {
                    this.alertChanged();
                    requestAnimationFrame(() => {
                        blocks?.forEach(block => {
                            if (block?.type?.key === BriefingBlockTypeEnum.CHART) {
                                this.alertsService.notifyBlockDisplayElementChanged(block);
                            }
                        });
                    });
                })
            ).subscribe(),
            // @TODO unhide to add precision is added to alerts
            // this.precisionClipService.watch(this.alert.id, PrecisionResourceType.ALERT).subscribe(status => {
            //     this.precisionClipProcess = status;
            // if (this.precisionClipProcess.status === 'completed') {
            //     const io = {
            //         inputs: {
            //             id: 'precision_clip_complete',
            //             successCount: this.precisionClipProcess.clipsCompleted,
            //             failedCount: this.precisionClipProcess.clipsFailed || 0,
            //             width: 600
            //         },
            //         outputs: {}
            //     };
            //     this.coreDialogService.open(PrecisionConfirmDialogComponent, io);
            // }
            // })
        );

        this.overlayContainer = document.getElementById('main-content');
    }

    @HostListener('window:beforeunload', ['$event'])
    pageUnload($event) {
        if (!this.alertsService.canLeaveCurrentAlert()) {
            $event.returnValue = this.localizationService.transform('DeactivateGuard.messageText');
        }
    }

    saveDraft(): void {
        this.disableSaveButton = true;
        if (!this.validate(this.draftErrors)) {
            this.disableSaveButton = false;
            return;
        }
        if (!this.alert.id) {
            const io: NewResourceDialogModel = {
                inputs: {
                    id: 'nameAlert',
                    heading: 'Alerts.nameAlertDialog.heading',
                    description: 'Alerts.renameAlertDialog.description',
                    placeholder: 'Alerts.renameAlertDialog.placeholder',
                    okLabel: 'Core.button.confirm',
                    formValidator: (name: string) => {
                        if (this.alertsService.isAlertNameDuplicate(name)) {
                            return [
                                'Alerts.copyAlertDuplicateNameErrorMessage'
                            ];
                        }

                        return [];
                    },
                    submit: (name: string, group?: DisplayGroupModel) => {
                        this.alert.name = name;
                        this.setSurgeValues();
                        this.alertsService.currentAlert.subject = this.alert.subject;
                        this.alert.groupId = group?.id;

                        return this.alertsService.saveCurrentAlert().pipe(
                            tap(isSaved => {
                                if (isSaved) {
                                    this.isDirty = false;
                                    this.resetValidation();
                                }
                                this.disableSaveButton = false;
                            }),
                            catchError((err) => {
                                this.disableSaveButton = false;
                                throw err;
                            })
                        );
                    }
                },
                outputs: {
                    dialogClose: () => {
                        this.disableSaveButton = false;
                    }
                }
            };
            this.coreDialogService.open(NewResourceDialogComponent, io);
        } else {
            this.alertsService.saveCurrentAlert().subscribe({
                next: (isSaved) => {
                    if (isSaved) {
                        this.isDirty = false;
                        this.resetValidation();
                    }
                    this.disableSaveButton = false;
                },
                error: () => this.disableSaveButton = false
            });
        }
    }

    setNewAlertNameDialog() {
        const textInputDialogConfig: TextInputDialogModel = {
            inputs: {
                heading: 'Alerts.dialogs.alertName.heading',
                description: 'Alerts.dialogs.alertName.description',
                placeholder: 'Alerts.dialogs.alertName.placeholder',
                okLabel: 'Alerts.dialogs.alertName.save',
                cancelLabel: 'Discover.dialogs.universal.cancelLabel',
                width: 300,
                submit: (name) => {
                    this.alert.name = name;

                    // sets the alertName for chiron twig
                    this.setSurgeValues();
                    this.saveDraft();
                    return of(null);
                },
                formValidator: (alertName: string) => {
                    this.alert.name = alertName.trim();
                    const duplicateAlertName = this.alertsService.isAlertNameDuplicate(this.alert.name, this.alert.id);
                    this.alert.name = null;

                    return duplicateAlertName ? ['Alerts.input.error.duplicateName'] : [];
                }
            }
        };
        this.coreDialogService.open(TextInputDialogComponent, textInputDialogConfig);
    }

    activate(): void {
        if (!this.validate(this.sendErrors)) {
            this.selectedTabIndex = 0;

            return;
        }
        if (!this.alert?.selectedDeliveryOption?.email && !this.alert?.selectedDeliveryOption?.rssFeed) {
            this.showDeliveryOptionsErrorMsg = true;
            this.selectedTabIndex = 0;
            return;
        }

        if (this.briefingConfigRequest) {
            this.briefingConfigRequest.unsubscribe();
        }

        this.briefingConfigRequest = gateCall(this.briefingConfigService.hasBriefingConfigAccess()).pipe(
            switchMap(() => this.briefingAccountApi.getConfig()),
            filter(x => !!x),
            tap((configModel) => {
                this.briefingConfigService.setConfig(configModel);

                if ((configModel.alertLimit === -1) || (configModel.alertLimit > configModel.activeAlertTotal)) {
                    this.showActivateDialog();
                } else {
                    this.showAlertLimitReachedDialog();
                }
            }),
            catchError((err) => {
                console.error('Failed to get briefing config', err);
                return of([]);
            })
        ).subscribe();
    }

    update(actionAfterSave?: AlertActionsEnum): void {
        this.disableSaveButton = true;

        const saveCurrentAlertRequest = () => {
            this.alertsService.saveCurrentAlert(!!actionAfterSave).subscribe({
                next: isSaved => {
                    if (isSaved) {
                        this.isDirty = false;
                        if (actionAfterSave === AlertActionsEnum.activate) {
                            this.confirmActivate();
                        } else if (actionAfterSave === AlertActionsEnum.pause) {
                            this.alertsService.pauseCurrentAlert();
                        } else if (actionAfterSave === AlertActionsEnum.renew) {
                            this.alertsService.renewCurrentAlert();
                        } else {
                            this.resetValidation();
                        }
                    }

                    this.disableSaveButton = false;
                },
                error: () => this.disableSaveButton = false
            });
        };

        if (this.alert.selectedDeliveryOption.email && !this.validate(this.sendErrors)) {
            this.disableSaveButton = false;
            if (this.alert.status === BriefingStatusEnum.ACTIVE) {
                this.coreDialogService.open(
                    ConfirmDialogComponent,
                    {
                        inputs: {
                            heading: 'Alerts.updateActive.warning.header',
                            alertMessage: 'Alerts.updateActive.warning.message',
                            alertType: messageSeverityEnum.warning,
                            okLabel: 'save',
                            cancelLabel: 'cancel',
                            width: 350
                        },
                        outputs: {
                            dialogClose: (confirmed: boolean) => {
                                if (confirmed) {
                                    actionAfterSave = AlertActionsEnum.pause;
                                    saveCurrentAlertRequest();
                                }
                            }
                        }
                    }
                );
            }
            return;
        }

        saveCurrentAlertRequest();
    }

    pause(): void {
        if (this.isDirty) {
            this.update(AlertActionsEnum.pause);
        } else {
            this.alertsService.pauseCurrentAlert();
        }
    }

    renew(): void {
        if (!this.validate(this.sendErrors)) {
            return;
        }
        if (this.isDirty) {
            this.update(AlertActionsEnum.renew);
        } else {
            this.alertsService.renewCurrentAlert();
        }
    }

    /**
     * Updates the time range when the alert is updated
     */
    updateTimeRangeFromAlert(): void {
        if (!this.alert) {
            return;
        }
        const range = this.alert.sendAt.range;
        if (range && (range.start || range.end)) {
            this.timeRange = {
                ongoing: false,
                start: range.start,
                end: range.end,
                timezone: this.alert.sendAt.timeZone || this.preferences.timeZone
            };
        } else {
            this.timeRange = {
                ongoing: true,
                start: null,
                end: null,
                timezone: this.preferences ? this.preferences.timeZone : null
            };
        }
    }

    /**
     * Adds the preferences time zone to the time range if another time zone is not already specified
     */
    updateTimeRangeTimezoneFromPreferences(): void {
        if (this.timeRange && !this.timeRange.timezone) {
            // need to do a new object so it updates properly
            this.timeRange = {
                ongoing: this.timeRange.ongoing,
                start: this.timeRange.start,
                end: this.timeRange.end,
                timezone: this.preferences ? this.preferences.timeZone : null
            };
        }
    }

    checkTimeRangeErrors(): void {
        if (this.timeRange.ongoing) {
            this.fieldErrors.timeRangeIncomplete = false;
            this.fieldErrors.timeRangePast = false;
            this.fieldErrors.endDateLessThanStartDate = false;
        } else {
            this.fieldErrors.timeRangeIncomplete = !this.timeRange.end;
            this.fieldErrors.timeRangePast = false;
            this.fieldErrors.endDateLessThanStartDate = false;

            if (!this.timeRange.end) {
                return;
            }

            if (this.timeRange.end < this.minEndDate) {
                this.fieldErrors.timeRangePast = true;
            }

            if (this.timeRange.end < this.timeRange.start) {
                this.fieldErrors.endDateLessThanStartDate = true;
            }
        }
    }

    /**
     * Handles when time range has been changed by user interactions
     */
    timeRangeChanged(): void {
        if (this.timeRange.ongoing) {
            this.alert.sendAt.range = null;
        } else {
            let range = this.alert.sendAt.range;
            if (!range) {
                range = new BriefingDateRangeModel();
                this.alert.sendAt.range = range;
            }
            range.start = this.timeRange.start;
            range.end = this.timeRange.end;
            this.alert.sendAt.timeZone = this.timeRange.timezone;
        }
        this.checkTimeRangeErrors();

        this.alertChanged();
    }

    sourceSelected(source: MonitoringBackEndDataSourceModel): void {
        if (source) {
            this.selectedSource = source;
            this.alert.alertIsSubscriptionSearch = false;
            this.alert.alertIsAgentSearch = false;
            this.alert.alertIsThemeSearch = false;
            this.alert.alertIsAiAgentSearch = false;
            this.alert.filterSearch.searchCriteria = source.filterSearchCriteria;

            switch (source.type) {
                case MonitoringSeriesTypeEnum.topic:
                    this.alert.searchCriteriaId = source.id;
                    break;

                case MonitoringSeriesTypeEnum.subscription:
                    this.alert.searchCriteriaId = source.id;
                    this.alert.alertIsSubscriptionSearch = true;
                    break;
                case MonitoringSeriesTypeEnum.socialTopic:
                    this.alert.searchCriteriaId = source.id;
                    this.alert.alertIsAgentSearch = true;
                    break;
                case MonitoringSeriesTypeEnum.theme:
                    this.alert.searchCriteriaId = source.id;
                    this.alert.alertIsThemeSearch = true;
                    break;
                case MonitoringSeriesTypeEnum.aiAgent:
                    this.alert.searchCriteriaId = source.id;
                    this.alert.alertIsAiAgentSearch = true;
                    break;
            }
            this.fieldErrors.noSource = false;
            if (this.sourceDeleted) {
                requestAnimationFrame(() => {
                    this.sourceDeleted = false;
                });
            }

            // we need to make sure there alert source is set and loaded from the API before we can open the dialog
            // and when you create a new MonitoringBackEndDataSourceModel, it creates a blank MentionSearchCriteriaModel as the
            // sourceSearchCriteria, but then clears some values...
            // on the initial load, sourceSelected() gets triggered twice (one for filter, one for source), and we need both calls to come back to work
            const newBlankSearch = new MentionSearchCriteriaModel();
            newBlankSearch.query.dateQuery = null;
            newBlankSearch.facets = MentionSearchCriteriaModel.accountSearchFacets;
            newBlankSearch.dateFacets = [];
            newBlankSearch.query.keywordGroups = [];

            this.sourceLoaded = !!source.id && !this.selectedSource.sourceSearchCriteria.equals(newBlankSearch);
        } else {
            this.selectedSource = null;
            this.alert.searchCriteriaId = null;
            this.fieldErrors.noSource = true;
            this.sourceLoaded = false;
        }

        this.filterError = '';
        this.fieldErrors.invalidFilter = false;
        this.updateCuratorBlockSource();
        this.alertChanged();
    }

    handleSourceNotFound(sourceNotFound: boolean) {
        if (sourceNotFound && this.alert.status === BriefingStatusEnum.ACTIVE) {
            this.pause();
        }
        requestAnimationFrame(() => {
            this.sourceDeleted = sourceNotFound;
        });
    }

    /**
     *
     * Handles a change to the delivery option of alert
     *
     */
    public deliveryOptionChanged(selected: boolean, value: deliveryOptionsEnum) {
        this.showDeliveryOptionsErrorMsg = false;
        this.alert.deliveryOptionChanged(selected, value);
        if (!this.alert?.selectedDeliveryOption?.rssFeed) {
            this.alert.feeds = [];
        }
        this.alertChanged();
    }

    /**
     * Handles a change to the subject of the alert
     */
    subjectChanged(value: string): void {
        this.fieldErrors.subjectEmpty = !value || value.trim().length === 0;
        this.fieldErrors.subjectLength = value && value.length === this.maxInputTextLength;

        this.alertChanged();
    }

    /**
     * Handles a change to the from name of the alert
     */
    fromNameChanged(value: string): void {
        this.fieldErrors.fromNameEmpty = !value || value.trim().length === 0;
        this.fieldErrors.fromNameLength = value && value.length === this.maxInputTextLength;

        this.alertChanged();
    }

    /**
     * Handles a change to the reply-to of the alert
     */
    replyToChanged(value: string): void {
        this.fieldErrors.replyToEmpty = !value || value.trim().length === 0;
        this.fieldErrors.replyToLength = value && value.length === this.maxInputTextLength;
        this.alertChanged();
    }

    /**
     *
     * Handles a change to the recipients of the alert
     */
    recipientsChanged(value: string[]): void {
        this.fieldErrors.recipientsEmpty = !value || value.length === 0;
        this.alertChanged();
    }

    /**
     *
     * Handles a change to the rss feed of the alert
     */
    rssFeedChanged(value: string[]): void {
        this.fieldErrors.rssFeedEmpty = !value || value.length === 0;
        this.alertChanged();
    }

    /**
     * Check's the reply-to format for validity when the user has completed changing it's value
     */
    checkReplyToFormat(): void {
        if (!this.alert.replyTo) {
            return;
        }
        this.fieldErrors.replyToInvalidFormat = !validateEmail(this.alert.replyTo);
    }

    /**
     * Appends an error message if its not already there
     */
    private addErrorMsg(errorMsg: string, errors: string[]) {
        if (errors.indexOf(errorMsg) === -1) {
            errors.push(errorMsg);
        }
    }

    /**
     * Check for errors on blocks of type text or title
     *
     * @param block the item to check for errors on
     *
     * @returns boolean - true if no errors
     */
    private checkErrorsForTextOrTitle(block: BlockModel): boolean {
        const displayElement = block.displayElement as TextDisplayElementModel;

        if (!displayElement.text || !displayElement.text.trim()) {
            if (block.type.key === AlertBlockTypeEnum.TEXT) {
                const errorMsg = 'Alerts.error.textRequired';
                this.addErrorMsg(errorMsg, block.errors);

                return false;
            }
            if (block.type.key === AlertBlockTypeEnum.TITLE ) {
                const errorMsg = 'Alerts.error.titleRequired';
                this.addErrorMsg(errorMsg, block.errors);

                return false;
            }
        }
        block.errors = [];

        return true;
    }

    /**
     * Check the current alert's blocks for errors
     *
     * @return boolean - true if no errors
     */
    public validateAlertBlocks(level: number): boolean {
        let isValid = true;
        const blocks = this.alert?.blocks;

        for (let i = 0; i < blocks?.length; i++) {
            const block = blocks[i];
            switch (block.type.key) {
                case AlertBlockTypeEnum.TITLE:
                case AlertBlockTypeEnum.TEXT:
                    isValid = this.checkErrorsForTextOrTitle(block) && isValid;
                    break;
                case AlertBlockTypeEnum.IMAGE:
                    isValid = this.imageBlockIsValid(block) && isValid;
                    break;
                case AlertBlockTypeEnum.CHART:
                    isValid = this.checkErrorsForChartBlock(block, level) && isValid;
                    break;
                case AlertBlockTypeEnum.ANALYSIS:
                    isValid = this.checkErrorsForAnalysisBlock(block, level) && isValid;
                    break;
                case AlertBlockTypeEnum.DATE:
                    break;
                case AlertBlockTypeEnum.COVERAGE_CURATOR:
                    (block as CuratorBlock).blocks?.forEach(child => {
                        if (child.type.key === AlertBlockTypeEnum.ANALYSIS) {
                            isValid = this.checkErrorsForAnalysisBlock(child, level) && isValid;
                        }
                    });
                    break;
                case AlertBlockTypeEnum.ONE_COLUMN_LAYOUT:
                case AlertBlockTypeEnum.TWO_COLUMN_LAYOUT:
                case AlertBlockTypeEnum.THREE_COLUMN_LAYOUT:
                    (block as LayoutBlock).blocks?.forEach(column => {
                        column.forEach(child => {
                            if (child.type.key === AlertBlockTypeEnum.ANALYSIS) {
                                isValid = this.checkErrorsForAnalysisBlock(child, level) && isValid;
                            }
                        });
                    });
                    break;
            }
        }

        return isValid;
    }

    /**
     * Resets the field validation error messages
     */
    private resetValidation(): void {
        this.showErrorsLevel = this.noErrors;

        this.fieldErrors.noSource = false;
        this.fieldErrors.nameEmpty = false;
        this.fieldErrors.nameLength = false;
        this.fieldErrors.subjectEmpty = false;
        this.fieldErrors.subjectLength = false;
        this.fieldErrors.fromNameEmpty = false;
        this.fieldErrors.fromNameLength = false;
        this.fieldErrors.replyToEmpty = false;
        this.fieldErrors.replyToLength = false;
        this.fieldErrors.replyToInvalidFormat = false;
        this.fieldErrors.recipientsEmpty = false;
        this.fieldErrors.rssFeedEmpty = false;
        this.fieldErrors.timeRangePast = false;
        this.fieldErrors.timeRangeIncomplete = false;
        this.fieldErrors.invalidFilter = false;

        this.minEndDate = new Date();
    }

    setBannerErrorList(level: number) {
        const errorArray: BannerErrorConfigModel[] = [];

        if (!this.validateAlertBlocks(level)) {
            const result = this.alertsService.getBlockErrors(this.alert.blocks);
            for (let i = 0; i < result?.length; i++) {
                errorArray.push(
                    {
                        errorId: result[i].id,
                        label: result[i].label
                    }
                );
            }
        }
        if (this.fieldErrors.noSource) {
            errorArray.push(bannerErrorsMap.get(AlertBannerErrorTypeEnum.ALERT_SOURCE_ERROR));
        }
        if (this.fieldErrors.nameEmpty || this.fieldErrors.nameLength) {
            errorArray.push(bannerErrorsMap.get(AlertBannerErrorTypeEnum.ALERT_NAME_ERROR));
        }
        if (this.alert?.selectedDeliveryOption?.email) {
            if (this.fieldErrors.subjectEmpty || this.fieldErrors.subjectLength) {
                errorArray.push(bannerErrorsMap.get(AlertBannerErrorTypeEnum.ALERT_SUBJECT_ERROR));
            }
            if (this.fieldErrors.fromNameEmpty || this.fieldErrors.fromNameLength) {
                errorArray.push(bannerErrorsMap.get(AlertBannerErrorTypeEnum.ALERT_FROMNAME_ERROR));
            }
            if (this.fieldErrors.replyToEmpty || this.fieldErrors.replyToLength || this.fieldErrors.replyToInvalidFormat) {
                errorArray.push(bannerErrorsMap.get(AlertBannerErrorTypeEnum.ALERT_REPLYTO_ERROR));
            }
            if (this.fieldErrors.recipientsEmpty) {
                errorArray.push(bannerErrorsMap.get(AlertBannerErrorTypeEnum.ALERT_RECIPIENT_ERROR));
            }
        }

        if (this.alert?.selectedDeliveryOption?.rssFeed) {
            if (this.fieldErrors.rssFeedEmpty) {
                errorArray.push(bannerErrorsMap.get(AlertBannerErrorTypeEnum.ALERT_RSS_FEED_ERROR));
            }
        }
        if (this.fieldErrors.timeRangePast || this.fieldErrors.timeRangeIncomplete) {
            errorArray.push(bannerErrorsMap.get(AlertBannerErrorTypeEnum.ALERT_SCHEDULING_ERROR));
        }

        if (this.fieldErrors.invalidFilter) {
            errorArray.push(bannerErrorsMap.get(AlertBannerErrorTypeEnum.ALERT_INVALID_FILTER));
        }

        this.bannerErrorList = errorArray;
    }

    isMessageExpanded(event: boolean) {
        this.isBannerExpanded = !event;
    }

    focusErrorElement(errorId: string) {
        requestAnimationFrame(() => {
            this.selectedTabIndex = this.defaultSelectedTabIndex;
            FocusHelper.focusError(errorId);
        });
    }


    /**
     * Validates the current alert data
     */
    public validate(level: number): boolean {
        this.showErrorsLevel = Math.max(this.showErrorsLevel, level);

        if (!this.alert) {
            return false;
        }

        this.fieldErrors.rssFeedEmpty = !this.alert.feeds || this.alert.feeds.length === 0;
        this.fieldErrors.invalidFilter = !!this.filterError;

        let isValid = true;
        if (level >= this.draftErrors) {
            if (this.alert?.selectedDeliveryOption?.rssFeed) {
                if (this.fieldErrors.rssFeedEmpty) {
                    this.showErrors = true;
                    isValid = false;
                }
            }

            if ( this.fieldErrors.invalidFilter) {
                isValid = false;
            }

            if (!isValid) {
                requestAnimationFrame(() => {
                    FocusHelper.focusFirstError(this.rhp.container.nativeElement, this.errorClass, this.rhp.scrollPanel);
                    this.setBannerErrorList(level);
                    if (this.bannerErrorList.length) {
                        this.isBannerExpanded = false;
                        this.isShowBannerMessage = true;
                    }
                });
                return false;
            }
        }

        if (level >= this.sendErrors) {
            // we don't want to show these errors on save draft (small usecase for rssFeedEmpty)
            this.fieldErrors.noSource = !this.alert.searchCriteriaId;
            this.fieldErrors.subjectEmpty = !this.alert.subject || this.alert.subject.trim().length === 0;
            this.fieldErrors.fromNameEmpty = !this.alert.fromName || this.alert.fromName.trim().length === 0;
            this.fieldErrors.replyToEmpty = !this.alert.replyTo || this.alert.replyTo.trim().length === 0;
            this.fieldErrors.recipientsEmpty = !this.alert.recipientList || this.alert.recipientList.length === 0;
            this.checkTimeRangeErrors();

            const hasBlockErrors = !this.validateAlertBlocks(level);
            if (this.alert?.selectedDeliveryOption?.email) {
                if (this.fieldErrors.subjectEmpty
                    || this.fieldErrors.fromNameEmpty
                    || this.fieldErrors.replyToEmpty
                    || this.fieldErrors.recipientsEmpty
                ) {
                    this.showErrors = true;
                    isValid = false;
                }
            }

            if (this.alert?.selectedDeliveryOption?.rssFeed) {
                if (this.fieldErrors.rssFeedEmpty) {
                    this.showErrors = true;
                    isValid = false;
                }
            }

            if (
                hasBlockErrors
                || this.fieldErrors.noSource
                || this.fieldErrors.timeRangePast
                || this.fieldErrors.timeRangeIncomplete
            ) {
                this.showErrors = true;
                isValid = false;
            }

            if (!isValid) {
                requestAnimationFrame(() => {
                    if (hasBlockErrors) {
                        this.pageBuilder.focusFirstBlockWithErrors();
                    } else {
                        FocusHelper.focusFirstError(this.rhp.container.nativeElement, this.errorClass, this.rhp.scrollPanel);
                    }
                    this.setBannerErrorList(level);
                    if (this.bannerErrorList.length) {
                        this.isBannerExpanded = false;
                        this.isShowBannerMessage = true;
                    }
                });
                return false;
            }
        }

        this.setBannerErrorList(this.showErrorsLevel);
        if (!this.bannerErrorList?.length) {
            this.isShowBannerMessage = false;
        }

        return true;
    }

    /**
     * Check for errors on an image block
     *
     * @param block the item to check for errors on
     *
     * @returns boolean - true if no errors
     */
    private imageBlockIsValid(block: BlockModel): boolean {
        const imageDisplayElement = (block.displayElement as BriefingImageDisplayElementModel);

        if (imageDisplayElement?.images?.length) {
            imageDisplayElement?.images.forEach(image => {
                const urlString = image.url;
                // Empty or unset url doesn't need to be validated.
                if ((!urlString || urlString.trim().length === 0) && image.imageSource !== ImageSourceEnum.AUTOMATE) {
                    const errorMsg = 'Briefings.error.imageRequired';
                    this.addErrorMsg(errorMsg, block.errors);

                    return false;
                }

                if (image.imageSource !== ImageSourceEnum.AUTOMATE) {
                    try {
                        // Pass a base so protocol-relative (//host/path) and other
                        // browser-resolvable URLs validate the same way <img> resolves them.
                        new URL(urlString, window.location.origin);
                    } catch {
                        const errorMsg = 'Briefings.error.invalidImageUrl';
                        this.addErrorMsg(errorMsg, block.errors);

                        return false;
                    }
                }
            });
        } else {
            // no images added
            const errorMsg = 'Briefings.error.imageRequired';
            this.addErrorMsg(errorMsg, block.errors);

            return false;
        }

        block.errors = [];

        return true;
    }


    /**
     * Check for errors on Chart block
     *
     * @param block the item to check errors on
     *
     * @returns boolean - true if no errors
     */
    private checkErrorsForChartBlock(block: BlockModel, level: number): boolean {
        const displayElement = block.displayElement as BriefingChartDisplayElementModel;
        block.errors = [];

        if (level >= this.draftErrors) {
            if (displayElement.chart.chartUrl?.length >= MAX_BASE_64_IMAGE_SIZE) {
                this.addErrorMsg('Briefings.error.chart.image.size', block.errors);
                return false;
            }
        }

        if (level >= this.sendErrors && !displayElement.canRender()) {
            this.addErrorMsg('Briefings.error.chart.required', block.errors);
            return false;
        }

        return true;
    }

    private checkErrorsForAnalysisBlock(block: BlockModel, level: number): boolean {
        block.errors = [];

        if (level >= this.sendErrors) {
            const displayElement = block.displayElement as AnalysisDisplayElementModel;
            if (!displayElement.generateOnSend && !displayElement.automaticallyGenerated && !displayElement.text?.trim()) {
                this.addErrorMsg('Alerts.error.analysisRequired', block.errors);
                return false;
            }
        }

        return true;
    }

    /**
     * Checks any image blocks in the alert for errors
     */
    private checkBlocksForImageErrors(): void {
        if (!this.alert) {
            return;
        }

        this.alert.blocks.forEach(block => {
            if (block.type.key === AlertBlockTypeEnum.IMAGE) {
                this.imageBlockIsValid(block);
            }
        });
    }

    /**
     * Shows the activate alert dialog
     */
    showActivateDialog(): void {
        this.activateDialogStatusChanged(true);
    }

    /**
     * Sends the activate alert action to the backend
     */
    confirmActivate(): void {
        this.alertsService.activateCurrentAlert().subscribe({
            next: () => {
                this.resetValidation();
                this.activateDialogStatusChanged(false);
            },
            error: () => this.activateDialogStatusChanged(false)
        });
    }

    /**
     * Opens the activate dialog with the proper metadata or closes the dialog
     */
    activateDialogStatusChanged(status: boolean): void {
        this.activateDialogMetadata = [];

        if (!status) {
            return;
        }

        this.activateDialogMetadata.push({
            label: 'Alerts.activateDialog.name',
            value: this.alert.name
        });
        if (this.alert.sendAt.range && this.alert.sendAt.range.start && this.alert.sendAt.range.end) {
            this.activateDialogMetadata.push({
                label: 'Alerts.activateDialog.range.start',
                value: moment(this.alert.sendAt.range.start)
                    .tz(this.alert.sendAt.timeZone)
                    .format(this.localizationService.transform('Alerts.activateDialog.range.dateFormat'))
            });
            this.activateDialogMetadata.push({
                label: 'Alerts.activateDialog.range.end',
                value: moment(this.alert.sendAt.range.end)
                    .tz(this.alert.sendAt.timeZone)
                    .format(this.localizationService.transform('Alerts.activateDialog.range.dateFormat'))
            });
        } else {
            this.activateDialogMetadata.push({
                label: 'Alerts.activateDialog.range',
                value: this.localizationService.transform('Alerts.activateDialog.range.ongoing')
            });
        }
        this.activateDialogMetadata.push({
            label: 'Alerts.activateDialog.recipients',
            value: this.alert.recipientList.join('; ')
        });

        if (this.alert.feeds.length) {
            this.activateDialogMetadata.push({
                label: 'Briefings.confirmationDialog.rssFeeds',
                value: this.alert.feeds,
                customTemplate: this.rssFeedDisclaimer
            });
        }

        const io: PublishStoryDialogModel = {
            inputs: {
                id: 'alerts.activateDialog',
                header: 'Alerts.activateDialog.heading',
                headerIcon: 'agpr-i-line-briefings',
                metadata: this.activateDialogMetadata,
                localization: this.activateDialogLocalization,
                disclaimers: [this.localizationService.transform('Alerts.activateDialog.legalDisclaimer')]
            },
            outputs: {
                dialogClose: (confirmed: boolean) => {
                    if (confirmed) {
                        if (this.isDirty) {
                            this.update(AlertActionsEnum.activate);
                        } else {
                            this.confirmActivate();
                        }
                    }
                }
            }
        };
        this.coreDialogService.open(PublishStoryComponent, io);

    }

    /**
     * Updates the service when the current alert has changed so it can be handled properly at all levels
     */
    alertChanged(): void {
        this.showErrors = false;
        this.isDirty = !this.alert || !this.alert.equals(this.alertsService.currentAlertCache);

        this.checkBlocksForImageErrors();
        this.setBannerErrorList(this.showErrorsLevel);
        if (!this.bannerErrorList?.length) {
            this.isShowBannerMessage = false;
        }
    }

    /**
     * Sets the attributes for alert details
     */
    public getAttributes(): void {
        if (!this.alert) {
            this.attributes = [];
            return;
        }

        const createdBy: AttributeModel = {
            id: BriefingDetailsEnum.createdBy,
            label: 'createdBy',
            value: this.alert.createdByUserId,
            type: attributeTypesEnum.user
        };

        const createdDate: AttributeModel = {
            id: BriefingDetailsEnum.createdDate,
            label: 'createdDate',
            value: this.alert.createdDate,
            type: attributeTypesEnum.date
        };

        const lastModifiedBy: AttributeModel = {
            id: BriefingDetailsEnum.lastModifiedBy,
            label: 'lastModifiedBy',
            value: this.alert.modifiedByUserId,
            type: attributeTypesEnum.user

        };

        const lastModifiedDate: AttributeModel = {
            id: BriefingDetailsEnum.lastModifiedDate,
            label: 'lastModifiedDate',
            value: this.alert.modifiedDate,
            type: attributeTypesEnum.date
        };

        this.attributes = [
            createdBy,
            createdDate,
            lastModifiedBy,
            lastModifiedDate
        ].filter(x => !!x.value);
    }

    getSurgeValues() {
        if (this.alert) {
            const surgeElement = this.alert.blocks.find(block => {
                return block.type.key === BriefingBlockTypeEnum.SURGE_ALERT;
            })?.displayElement as AlertSurgeDisplayElementModel;

            if (surgeElement) {
                this.metric = surgeElement?.metric;
                this.sensitivity = surgeElement?.sensitivity;
                this.coolDownPeriod = surgeElement?.coolDownPeriod;
                surgeElement.alertName = this.alert.name;

                this.alert.subject = (this.alert.name ? this.alert.name : '[Coverage Alert Name]') + ' - ' +
                    (this.metric ? this.localizationService.transform('Alerts.surge.metrics.' + this.metric) : '[Alert Metric Name]') + ' Surge Alert';
            }
        }
    }

    setSurgeValues() {
        if (this.alert) {
            const surgeElement = this.alert.blocks.find(block => {
                return block.type.key === BriefingBlockTypeEnum.SURGE_ALERT;
            })?.displayElement as AlertSurgeDisplayElementModel;

            if (surgeElement) {
                surgeElement.metric = this.metric;
                surgeElement.sensitivity = this.sensitivity;
                surgeElement.coolDownPeriod = this.coolDownPeriod;
                surgeElement.alertName = this.alert.name;

                this.alert.subject = (this.alert.name ? this.alert.name : '[Coverage Alert Name]') + ' - ' +
                    (this.metric ? this.localizationService.transform('Alerts.surge.metrics.' + this.metric) : '[Alert Metric Name]') + ' Surge Alert';
            }

            this.alertChanged();
        }
    }

    metricChanged() {
        if (this.metric === AlertSurgeMetricEnum.SENTIMENT) {
            this.sensitivity = 120;
        } else {
            this.sensitivity = 200;
        }

        this.setSurgeValues();
    }

    sensitivityChanged() {
        this.setSurgeValues();
    }

    coolDownChanged() {
        this.setSurgeValues();
    }

    showSensitivityPanel() {
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        const surgeElement = this.alert.blocks.find(block => {
            return block.type.key === BriefingBlockTypeEnum.SURGE_ALERT;
        })?.displayElement as AlertSurgeDisplayElementModel;

        this.coreDialogService.open(AlertSurgeSensitivityDialogComponent, {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: this.overlayContainer,
                title: 'Alerts.surge.metric.sensitivity.dialog.title',
                metric: surgeElement?.metric,
                sensitivity: surgeElement?.sensitivity,
                selectedSource: this.selectedSource
            },
        }, this.injector);
    }

    showAlertLimitReachedDialog() {
        this.coreDialogService.open(AlertLimitReachedDialogComponent);
    }

    updateCuratorBlockSource() {
        this.alert.blocks.forEach(block => {
            if (block.type.key === BriefingBlockTypeEnum.COVERAGE_CURATOR) {
                const curatorBlock = block as CuratorBlock;
                curatorBlock.blocks?.forEach(child => {
                    if (child.type.key === BriefingBlockTypeEnum.AUTOMATIC_MENTION_LIST) {
                        child.displayElement['backendDataSource'] = curatorBlock.displayElement.backendDataSource;
                    }
                });
            }
        });
    }

    // @TODO unhide to add precision is added to alerts
    // cancelPrecision() {
    //     this.precisionClipService.cancel(this.alert.id, PrecisionResourceType.ALERT);
    // }

    ngOnDestroy(): void {
        this.subscriptions.forEach((sub) => sub.unsubscribe());
        if (this.briefingConfigRequest) {
            this.briefingConfigRequest.unsubscribe();
        }
    }
}
